import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Star, Clock } from "lucide-react";
import { toast } from "sonner";

function StarPicker({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          onClick={() => onChange(n)}
          className="p-1 rounded hover:bg-primary/10 transition-colors"
          data-testid={`${testId}-star-${n}`}
          aria-label={`${n} stars`}
        >
          <Star
            size={26}
            strokeWidth={1.5}
            className={n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}
          />
        </button>
      ))}
    </div>
  );
}

export default function ChefRatingWidget() {
  const [state, setState] = useState(null); // {eligible, last, next_at}
  const [stars, setStars] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/customer/chef-rating-status");
      setState(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (stars < 1) { toast.error("Pick a rating between 1 and 5 stars"); return; }
    setBusy(true);
    try {
      await api.post("/customer/chef-rating", { stars, feedback });
      toast.success("Thanks — your rating has been submitted.");
      setStars(0); setFeedback("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't submit rating");
    } finally { setBusy(false); }
  };

  if (!state) return null;
  if (state.reason === "no_active_chef") return null;

  const nextDate = state.next_at ? new Date(state.next_at) : null;
  const daysUntil = nextDate ? Math.max(0, Math.ceil((nextDate - new Date()) / 86400000)) : 0;

  return (
    <div className="card-default mt-6" data-testid="chef-rating-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Star size={18} className="text-amber-500 fill-amber-400"/>
          <h4 className="heading text-lg font-semibold">Rate your chef</h4>
          <Badge variant="outline" className="rounded-full text-[10px]">Every 15 days</Badge>
        </div>
        {state.last && (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="rating-last">
            <Clock size={12}/> Last rated: {state.last.stars}★ on {new Date(state.last.created_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {state.eligible ? (
        <div className="space-y-3" data-testid="rating-active">
          <StarPicker value={stars} onChange={setStars} testId="rating-stars" />
          <Textarea
            placeholder="Optional feedback — punctuality, taste, hygiene…"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={2}
            data-testid="rating-feedback"
          />
          <div className="flex items-center gap-2">
            <Button
              className="rounded-full bg-primary hover:bg-primary/90"
              onClick={submit}
              disabled={busy || stars < 1}
              data-testid="rating-submit"
            >
              {busy ? "Submitting…" : "Submit rating"}
            </Button>
            <span className="text-xs text-muted-foreground">Your feedback helps us assign the right chef.</span>
          </div>
        </div>
      ) : (
        <div className="p-3 rounded-lg bg-muted border border-border text-sm text-muted-foreground" data-testid="rating-locked">
          <Clock size={14} className="inline mr-1"/>
          Next rating unlocks in <b>{daysUntil} day{daysUntil === 1 ? "" : "s"}</b>
          {nextDate && <> ({nextDate.toLocaleDateString()})</>}.
        </div>
      )}
    </div>
  );
}
